/**
 * Maps this app's light/dark choice onto a Carbon theme.
 *
 * `ThemeProvider` (ThemeContext.tsx) owns the choice and writes it to
 * `<html data-theme>`; the `:root` blocks in styles/carbon.scss theme the
 * document from that attribute. This component themes the React tree, which
 * is what Carbon components themselves read — they take tokens from the
 * nearest `<Theme>` context, not from the document.
 *
 * Both are needed: without the CSS blocks the body background and anything
 * portalled outside the tree stay unthemed; without this component Carbon's
 * own components fall back to the white theme in dark mode.
 *
 * Carbon also exports a `useTheme` hook. This module deliberately imports only
 * `Theme` — import Carbon's hook aliased (`useTheme as useCarbonTheme`)
 * anywhere both are needed, since `./ThemeContext` exports that name too.
 */
import { Theme } from '@carbon/react';
import type { ReactNode } from 'react';
import { useTheme } from './ThemeContext';

/** Carbon ships four themes; this app uses the two endpoints. `g100` is #161616, not pure black. */
export type CarbonTheme = 'white' | 'g10' | 'g90' | 'g100';

export function CarbonThemeBridge({ children }: { children: ReactNode }): React.JSX.Element {
  const { theme } = useTheme();
  return <Theme theme={theme === 'dark' ? 'g100' : 'white'}>{children}</Theme>;
}

export default CarbonThemeBridge;
